/**
 * Decimal precision for the rate fields collected across the tender flow.
 *
 * Two different limits, because the two families of rate differ in magnitude by orders of
 * magnitude:
 *
 *   Energy rates (peak / off-peak / shoulder, c/kWh)  — 5 dp
 *     Quoted around 7-12 c/kWh. Retailers occasionally quote a 5th decimal, and rounding it
 *     away moves a large site's annual cost by real dollars.
 *
 *   Environmental rates (LREC / SREC / VEEC / ESC / REPS ..., c/kWh)  — 9 dp
 *     Derived as `cert_price x power% / 1000`, which lands around 0.0067 c/kWh. At 4 dp that
 *     is only two significant figures — 0.00675100 rounded to 0.0068 is a 0.7% error on every
 *     environmental line, and a rate like 0.00047 collapses to 0.0005. 9 dp preserves the
 *     full derived value.
 *
 * ⚠️ These bounds must agree in three places or precision is silently lost at whichever one
 * is tightest: the input's `step` attribute (the browser rejects finer input), the rounding
 * applied when a rate is derived or stored, and the formatter used to display it. Import from
 * here rather than hard-coding — a literal `0.0001` / `toFixed(4)` anywhere in the rate path
 * re-introduces the ceiling for that one field only, which is how a value can look correct on
 * one step and wrong on the next.
 *
 * Deliberately free of `import.meta` / Node globals so both the React app and the Express
 * server can import it (see envSchemeRegistry.ts for the same arrangement).
 */

/** Max decimals for peak / off-peak / shoulder / anytime energy rates. */
export const ENERGY_RATE_DECIMALS = 5;

/** Max decimals for environmental certificate rates (LREC/SREC/VEEC/ESC/REPS/...). */
export const ENV_RATE_DECIMALS = 9;

/**
 * Max decimals for a certificate PRICE ($/certificate) — 5 dp.
 *
 * A different quantity from the rates above: dollars per certificate, quoted $3-$100. Most
 * retailers round it ($9.25, $40.00) which is why a 2 dp ceiling here went unnoticed, but AGL
 * publishes a blended price per deal — 8.721, 32.7874, 35.4058 — and Origin quotes 5 dp.
 * Measured on prod, 906 stored certificate prices carry more than 2 decimals.
 *
 * It matters because the price is not decoration: `rate = cert x power% / 1000` is
 * definitional, so a certificate price rounded from 32.7874 to 32.79 moves the derived
 * environmental rate and every dollar built on it.
 */
export const CERT_PRICE_DECIMALS = 5;

/**
 * Max decimals for a power percentage (%) — 6 dp.
 *
 * Legislated values are short (16.67, 19.17, 11.5) but a percentage DERIVED from a printed
 * rate is not: the parser computes `power% = rate_per_kwh x 1000 / cert_price`, which lands on
 * figures like 16.666667. 20 stored percentages on prod exceed 2 decimals.
 */
export const POWER_PCT_DECIMALS = 6;

/** `step` for an <input type="number"> holding an energy rate. */
export const ENERGY_RATE_STEP = '0.00001';

/** `step` for an <input type="number"> holding an environmental rate. */
export const ENV_RATE_STEP = '0.000000001';

/**
 * `step` for an <input type="number"> holding a certificate price.
 *
 * ⚠️ This was `step="0.01"` and that alone was the whole defect. A number input rejects any
 * value finer than its step, so AGL's 32.7874 could not be entered, and re-saving a year whose
 * price the parser had read correctly silently coarsened it to 32.79.
 */
export const CERT_PRICE_STEP = '0.00001';

/** `step` for an <input type="number"> holding a power percentage. */
export const POWER_PCT_STEP = '0.000001';

/**
 * Round to a decimal count without the float artefacts of `Math.round(n * 10**d) / 10**d`.
 *
 * That naive form breaks down at the precision we need here: `0.00675100 * 1e9` is
 * 6751000.000000001 in IEEE-754, and at 9 dp the error is large enough to change the result.
 * Going through the decimal string representation avoids it — toFixed rounds on the decimal
 * value, then Number() drops trailing zeros.
 */
const roundTo = (value: number, decimals: number): number => {
  if (!isFinite(value)) return value;
  return Number(value.toFixed(decimals));
};

/** Round an energy rate (peak/off-peak/shoulder) to the collected precision. */
export const roundEnergyRate = (value: number): number => roundTo(value, ENERGY_RATE_DECIMALS);

/** Round an environmental certificate rate to the collected precision. */
export const roundEnvRate = (value: number): number => roundTo(value, ENV_RATE_DECIMALS);

/**
 * Display an energy rate. Trailing zeros are trimmed so the common 4-dp quote still reads
 * "9.827" rather than "9.82700" — the extra precision only shows when a retailer actually
 * quoted it.
 */
export const formatEnergyRate = (value: number | null | undefined): string => {
  if (value == null || !isFinite(Number(value))) return '';
  return String(roundEnergyRate(Number(value)));
};

/** Display an environmental rate, trailing zeros trimmed. */
export const formatEnvRate = (value: number | null | undefined): string => {
  if (value == null || !isFinite(Number(value))) return '';
  return String(roundEnvRate(Number(value)));
};

/** Round a certificate price ($/certificate) to the collected precision. */
export const roundCertPrice = (value: number): number => roundTo(value, CERT_PRICE_DECIMALS);

/** Round a power percentage to the collected precision. */
export const roundPowerPct = (value: number): number => roundTo(value, POWER_PCT_DECIMALS);

/** Display a certificate price, trailing zeros trimmed — "40" not "40.00000". */
export const formatCertPrice = (value: number | null | undefined): string => {
  if (value == null || !isFinite(Number(value))) return '';
  return String(roundCertPrice(Number(value)));
};

/** Display a power percentage, trailing zeros trimmed. */
export const formatPowerPct = (value: number | null | undefined): string => {
  if (value == null || !isFinite(Number(value))) return '';
  return String(roundPowerPct(Number(value)));
};
